// Lib
import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';

// Services
import { SessionStorageService } from './session-storage.service';

// Constants
import { SESSION_STORAGE_KEYS as SK } from '../../constants';

// Helpers
import { filters } from '../../helpers/arrays';
import { filter } from 'rxjs/operators';

@Injectable()
export class PathStorageService {
    /**
     * Previous section reference.
     */
    protected previousSection: string;

    /**
     * Gets the current section.
     */
    get currentSection (): string {
        const url = this.router.url;
        const section = this.sectionFromUrl(url);
        this.previousSection = section;
        return section;
    }

    /**
     * Initiates the class.
     *
     * @param {SessionStorageService} sessionStorage - The session storage service.
     * @param {Router} router - The router instance.
     */
    constructor (
        private sessionStorage: SessionStorageService,
        private router: Router,
    ) {
        router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(() => {
                const prev = this.previousSection;
                const next = this.sectionFromUrl(this.router.url);
                this.clean(prev, next);
            });
    }

    /**
     * Gets the current section from the given URL.
     *
     * @param {string}
     * @return {string}
     */
    private sectionFromUrl (url: string): string {
        const regex = /^(\/?[^\/#?]+)/;
        return (regex.exec(url) || ['/'])[0];
    }

    /**
     * Gets all the keys belonging to the given section.
     *
     * @param {string} section - The section to get the keys for.
     * @return {string[]} - The keys that belong to the section.
     */
    private getSection (section: string): string[] {
        return this.sessionStorage.has(section)
            ? this.sessionStorage.get(section)
            : [];
    }

    /**
     * Stores the given keys against the given section.
     *
     * @param {string} section - The section to use.
     * @param {string[]} stored - The keys to set against the section.
     * @return {boolean} - Boolean to determine if the keys were successfully stored.
     */
    private setSection (section: string, stored: string[]): boolean {
        return this.sessionStorage.set(
            section,
            stored.filter(filters.unique),
        );
    }

    /**
     * Cleans up the path storage keys. Removes
     * any data relating to any section that is not
     * the current section.
     *
     * @param {string} prev - The previous section
     * @param {string} next - The next/current section
     */
    private clean (prev: string|null, next: string) {
        if (prev && prev !== next) {
            const sectionKey = this.sectionKey(prev);
            const keys = this.getSection(sectionKey).map(this.key);

            this.sessionStorage.removeMany(sectionKey, ...keys);
        }
    }

    /**
     * Prefixes the given key with the prefix in
     * the constants file.
     *
     * @param {string} key - The key to prefix.
     * @return {string} - The prefixe key.
     */
    private key (key: string): string {
        if (key.startsWith(SK.PATH_STORAGE.PREFIX)) {
            throw new Error('The key is already prefixed!');
        }

        return SK.PATH_STORAGE.PREFIX + key;
    }

    /**
     * Gets the key to use for the current section.
     *
     * @return {string} - The key.
     */
    private sectionKey (section: string): string {
        return `${SK.PATH_STORAGE.SECTION_PREFIX}(${section})`;
    }

    /**
     * Adds the given key to the current section.
     *
     * @param {string} key - The key to add.
     */
    private addKeyToCurrentSection (key: string) {
        const sectionKey = this.sectionKey(this.currentSection);
        const stored = this.getSection(sectionKey);

        this.setSection(sectionKey, [...stored, key]);
    }

    /**
     * Takes a key and a value, and saves it into local storage
     * as prefix__key == value.
     *
     * @param {string} key - The key to use in storage.
     * @param {string|object|number} value - The value to store.
     *
     * @return {boolean} - true/false, depending on whether the item was stored successfully or not.
     */
    public set (key: string, value: string|object|number): boolean {
        const saved = this.sessionStorage.set(this.key(key), value);

        if (saved) {
            this.addKeyToCurrentSection(key);
        }

        return saved;
    }

    /**
     * Gets the given key out of storage.
     *
     * @param {string} key - The key to retrieve.
     * @return {string|object|number|null} - The value from storage.
     */
    public get <T = string|object|number|null> (key: string): T {
        return this.has(key)
            ? this.sessionStorage.get(this.key(key))
            : null;
    }

    /**
     * Determines if a given key exists in storage.
     *
     * @param {string} key - The key to check.
     * @return {boolean} - Boolean to determine if the key exists or not.
     */
    public has (key: string): boolean {
        return this.sessionStorage.has(this.key(key));
    }
}
